/**
 * DiscountsPage - Página de descuentos y ofertas exclusivas
 * Muestra afiliaciones y códigos de descuento para la comunidad
 * Ruta: /descuentos
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import CardGiftcardRoundedIcon from "@mui/icons-material/CardGiftcardRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import ConfirmationNumberRoundedIcon from "@mui/icons-material/ConfirmationNumberRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import { useFirebase } from "../context/ApiContext";
import { DashboardLayout } from "../components/Dashboard";
import { BasicModal } from "../components/BasicModal/BasicModal";
import Auth from "../components/Auth/Auth";
import "./DiscountsPage.css";

/* ────────────────────────────────────────────
   Datos de las ofertas / afiliaciones
   (Edita aquí para añadir o modificar deals)
   ──────────────────────────────────────────── */
const DEALS = [
	{
		id: "iati",
		name: "IATI Seguros",
		category: "Seguro de viaje",
		discount: "5% OFF",
		logoIcon: ShieldRoundedIcon,
		logoBg: "linear-gradient(135deg, #DBEAFE, #BFDBFE)",
		description:
			"El seguro de viaje que utilizamos y recomendamos. Cubre asistencia médica, cancelaciones, equipaje perdido y mucho más. Viaja tranquilo sabiendo que estás cubierto ante cualquier imprevisto.",
		highlights: [
			{ label: "Asistencia 24h", color: "default" },
			{ label: "Diferentes tipos de coberturas", color: "blue" },
		],
		ctaText: "Obtener 5% de descuento",
		ctaLink: "https://www.iatiseguros.com/?r=97300500080093",
		ctaColor: "primary-blue",
		subtext: "Se aplica automáticamente al usar nuestro enlace",
	},
	{
		id: "getyourguide",
		name: "GetYourGuide",
		category: "Tours y actividades",
		logoIcon: ConfirmationNumberRoundedIcon,
		logoBg: "linear-gradient(135deg, #FEF3C7, #FDE68A)",
		description:
			"Miles de tours, visitas guiadas y experiencias en todo el mundo. Reserva entradas a monumentos, tours en español y actividades con cancelación gratuita. Ideal para planificar tu viaje con antelación.",
		highlights: [
			{ label: "Cancelación gratuita", color: "default" },
			{ label: "Tours en español", color: "amber" },
			{ label: "Entradas sin colas", color: "blue" },
		],
		ctaText: "Ver tours y actividades",
		ctaLink: "https://www.getyourguide.com/",
		ctaColor: "primary-amber",
		subtext: "Reserva con el mayor catálogo de experiencias",
	},
	{
		id: "civitatis",
		name: "Civitatis",
		category: "Tours y actividades",
		logoIcon: AccountBalanceRoundedIcon,
		logoBg: "linear-gradient(135deg, #E0E7FF, #C7D2FE)",
		description:
			"Tours, visitas guiadas y excursiones en español por todo el mundo. Entradas a museos y monumentos, free tours y experiencias con guías locales. Perfecto para descubrir cada destino.",
		highlights: [
			{ label: "Guías en español", color: "default" },
			{ label: "Free tours", color: "purple" },
			{ label: "Entradas y excursiones", color: "blue" },
		],
		ctaText: "Explorar tours y excursiones",
		ctaLink: "https://www.civitatis.com/",
		ctaColor: "primary-purple",
		subtext: "Miles de actividades en todo el mundo",
	},
	// Añade más deals aquí siguiendo la misma estructura:
	// {
	//   id: "ejemplo",
	//   name: "Nombre del servicio",
	//   category: "Categoría",
	//   discount: "X% OFF",
	//   logo: "emoji o texto",
	//   logoBg: "linear-gradient(...)",
	//   description: "Descripción...",
	//   highlights: [{ label: "Tag", color: "default|amber|blue|purple" }],
	//   ctaText: "Texto del botón",
	//   ctaLink: "https://...",
	//   ctaColor: "primary-amber|primary-blue|primary-green|primary-purple|primary-rose",
	//   subtext: "Texto pequeño bajo el botón",
	// },
];

const DiscountsPage = () => {
	const navigate = useNavigate();
	const { enqueueSnackbar } = useSnackbar();
	const { user, handleLogout: contextLogout } = useFirebase();

	const [showLogin, setShowLogin] = useState(false);
	const [authMode, setAuthMode] = useState("login");

	const formattedUser = user
		? {
				id: user.uid || user.id,
				displayName:
					user.displayName ||
					user.name ||
					user.email?.split("@")[0] ||
					"Usuario",
				email: user.email,
			}
		: null;

	const handleSectionChange = (sectionId) => {
		if (sectionId === "home") {
			navigate("/");
			return;
		}
		if (sectionId === "explore") {
			navigate("/explorar");
			return;
		}
		if (sectionId === "my-guides") {
			if (!user) {
				setAuthMode("login");
				setShowLogin(true);
				enqueueSnackbar("Inicia sesión para ver tus guías", {
					variant: "info",
				});
				return;
			}
			navigate("/mis-guias");
			return;
		}
		if (sectionId === "favorites") {
			if (!user) {
				setAuthMode("login");
				setShowLogin(true);
				enqueueSnackbar("Inicia sesión para ver tus favoritos", {
					variant: "info",
				});
				return;
			}
			navigate("/favoritos");
			return;
		}
		if (sectionId === "passport") {
			navigate("/passport");
			return;
		}
		if (sectionId === "travel-card") {
			navigate("/tarjeta-viajera");
			return;
		}
		if (sectionId === "create") {
			navigate("/crear-guia");
			return;
		}
		if (sectionId === "recent") {
			navigate("/");
			return;
		}
		if (sectionId === "modules") {
			if (!user) {
				setAuthMode("login");
				setShowLogin(true);
				enqueueSnackbar("Inicia sesión para ver tus módulos", { variant: "info" });
				return;
			}
			navigate("/modulos");
			return;
		}
		if (sectionId === "discounts") {
			// Ya estamos aquí
			return;
		}
		if (sectionId === "help") {
			navigate("/", { state: { section: "help" } });
			return;
		}
		if (sectionId === "settings") {
			if (!user) {
				setAuthMode("login");
				setShowLogin(true);
				enqueueSnackbar("Inicia sesión para acceder a la configuración", {
					variant: "info",
				});
				return;
			}
			navigate("/settings");
			return;
		}
	};

	const handleLogout = async () => {
		await contextLogout();
		enqueueSnackbar("Sesión cerrada", { variant: "success" });
	};

	return (
		<>
			<DashboardLayout
				user={formattedUser}
				activeSection="discounts"
				onSectionChange={handleSectionChange}
				onLogin={() => {
					setAuthMode("login");
					setShowLogin(true);
				}}
				onSignUp={() => {
					setAuthMode("signup");
					setShowLogin(true);
				}}
				onLogout={handleLogout}
				onCreateNew={() => navigate("/crear-guia")}
			>
				<div className="discounts-page">
					{/* ═══ HERO SECTION ═══ */}
					<div className="discounts-hero">
						<div className="discounts-hero-badge">
							★ Exclusivo para nuestra comunidad
						</div>
						<div className="discounts-hero-header">
							<CardGiftcardRoundedIcon
								className="discounts-hero-icon"
								aria-label="ofertas"
							/>
							<h1>
								Descuentos <span>exclusivos</span> para tu viaje
							</h1>
						</div>
						<p>
							Todos los descuentos se aplican automáticamente a través de
							nuestros enlaces.
						</p>
					</div>

					{/* ═══ DEALS GRID ═══ */}
					<div className="discounts-grid">
						{DEALS.map((deal) => {
							const LogoIcon = deal.logoIcon;
							return (
								<div className="deal-card" key={deal.id}>
									{/* Badge de descuento: solo si hay texto */}
									{deal.discount && (
										<div className="deal-discount-badge">{deal.discount}</div>
									)}

									{/* Header visual */}
									<div className="deal-card-visual">
										<div
											className="deal-logo-wrapper"
											style={{ background: deal.logoBg }}
										>
											{LogoIcon ? (
												<LogoIcon
													sx={{
														fontSize: 32,
														color: "rgba(26, 26, 46, 0.85)",
													}}
												/>
											) : (
												deal.logo
											)}
										</div>
										<div>
											<span className="deal-category">{deal.category}</span>
											<h3>{deal.name}</h3>
										</div>
									</div>

									{/* Body */}
									<div className="deal-card-body">
										<p>{deal.description}</p>
										{deal.highlights && deal.highlights.length > 0 && (
											<div className="deal-highlights">
												{deal.highlights.map((hl, idx) => (
													<span
														key={idx}
														className={`deal-highlight-tag ${hl.color || ""}`}
													>
														✓ {hl.label}
													</span>
												))}
											</div>
										)}
									</div>

									{/* CTA */}
									<div className="deal-card-footer">
										<a
											href={deal.ctaLink}
											target="_blank"
											rel="noopener noreferrer"
											className={`deal-cta-button ${deal.ctaColor}`}
										>
											{deal.ctaText}
											<span style={{ fontSize: "1.1em" }}>→</span>
										</a>
										{deal.subtext && (
											<span className="deal-cta-subtext">{deal.subtext}</span>
										)}
									</div>
								</div>
							);
						})}
					</div>

					{/* ═══ TRUST / TRANSPARENCIA ═══ */}
					<div className="discounts-trust">
						<p>
							Algunos de estos enlaces son de afiliados, lo que significa que
							recibimos una pequeña comisión si realizas una compra a través de
							ellos.{" "}
							<strong>
								Esto no tiene ningún coste extra para ti y nos ayuda a seguir
								creando contenido.
							</strong>{" "}
							Solo recomendamos productos y servicios que hemos probado
							personalmente.
						</p>
					</div>
				</div>
			</DashboardLayout>

			{/* Auth Modal */}
			<BasicModal
				width={500}
				showModal={showLogin}
				handleClose={() => setShowLogin(false)}
			>
				<Auth setShowLogin={setShowLogin} initialMode={authMode} />
			</BasicModal>
		</>
	);
};

export default DiscountsPage;
